<template>
  <div class="card ba-card">
    <!-- Title bar in the theme's highlight colour, fixed height so all charts line up. -->
    <div class="ba-card-header ba-accent-header">
      <h2 class="ba-card-title">
        <span class="ba-card-title-text" :title="title">{{ title }}</span>
        <span v-if="asterisk" class="ba-asterisk" aria-hidden="true">*</span>
        <button
          v-if="$slots.info"
          :id="infoId"
          type="button"
          class="btn btn-link btn-sm ba-info"
          :aria-label="`About ${title}`"
        >
          <i class="fa fa-info-circle" aria-hidden="true"></i>
        </button>
      </h2>
      <div class="ba-card-actions">
        <slot name="controls"></slot>
        <span v-if="badge" class="badge badge-light ba-badge">{{ badge }}</span>
        <button
          v-if="hasData"
          type="button"
          class="btn btn-sm ba-view-toggle"
          :aria-pressed="showTable ? 'true' : 'false'"
          @click="showTable = !showTable"
        >
          <i :class="showTable ? 'fa fa-line-chart' : 'fa fa-table'" aria-hidden="true"></i>
          {{ showTable ? "Chart" : "Table" }}
        </button>
      </div>
    </div>

    <b-popover v-if="$slots.info" :target="infoId" triggers="click blur" placement="bottom">
      <div class="ba-info-body"><slot name="info"></slot></div>
    </b-popover>

    <div class="ba-card-body">
      <p v-if="subtitle" class="ba-subtitle">{{ subtitle }}</p>
      <div v-if="loading" class="ba-state">
        <b-spinner small label="Loading"></b-spinner>
        <span class="ml-2">Loading…</span>
      </div>
      <div v-else-if="error" class="ba-state ba-state-error" role="alert">
        <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>{{ error }}
      </div>
      <div v-else-if="!hasData" class="ba-state">{{ emptyText }}</div>
      <div v-else-if="showTable" class="ba-table-wrap">
        <table class="table table-sm ba-table">
          <thead>
            <tr>
              <th scope="col">Month</th>
              <th v-for="col in table.columns" :key="col" scope="col" class="text-right">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in table.rows" :key="row.label">
              <th scope="row">
                {{ row.label }}
                <small v-if="row.note" class="text-muted">({{ row.note }})</small>
              </th>
              <td v-for="(cell, i) in row.cells" :key="i" class="text-right">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <v-chart v-else class="ba-chart" :option="option" autoresize @click="$emit('chart-click', $event)" />
    </div>
  </div>
</template>

<script>
import VueECharts from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart, ScatterChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";

use([CanvasRenderer, LineChart, BarChart, ScatterChart, GridComponent, TooltipComponent, LegendComponent]);

let nextId = 0;

/**
 * One Business Analytics chart card: a title bar in the theme's highlight colour with the
 * chart ⇄ table toggle and optional controls (the `controls` slot, e.g. a currency picker),
 * and loading / error / empty states. Explanatory notes go in the `info` slot (shown from the ⓘ
 * button) so every card keeps the same size. The table view is the accessible alternative to
 * the chart and must carry the same numbers.
 */
export default {
  name: "AnalyticsChartCard",
  components: { "v-chart": VueECharts },
  props: {
    title: { type: String, required: true },
    asterisk: { type: Boolean, default: false },
    /** One line under the title bar, e.g. what the chart leaves out. */
    subtitle: { type: String, default: "" },
    /** Short figure shown in the title bar, e.g. "+ 830 before tracking". */
    badge: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    error: { type: String, default: "" },
    emptyText: { type: String, default: "No data for this period." },
    /** ECharts option; null when there is nothing to draw. */
    option: { type: Object, default: null },
    /** { columns: string[], rows: Array<{ label, note?, cells: string[] }> } */
    table: { type: Object, default: () => ({ columns: [], rows: [] }) },
  },
  data() {
    nextId += 1;
    return { showTable: false, infoId: `ba-info-${nextId}` };
  },
  computed: {
    hasData() {
      return Boolean(this.option) && this.table.rows.length > 0;
    },
  },
};
</script>

<style scoped>
/* Every card is the same size; width comes from the page grid, height scales with the screen. */
.ba-card {
  height: clamp(420px, 60vh, 720px);
  display: flex;
  flex-direction: column;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  border: 0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(33, 47, 63, 0.12);
}
.ba-card-header {
  flex: 0 0 auto;
  height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
}
/* One line, so a long title never pushes this card's chart down. */
.ba-card-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: baseline;
  min-width: 0;
  color: inherit;
}
.ba-card-title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ba-asterisk {
  margin-left: 2px;
  flex: 0 0 auto;
}
.ba-info {
  flex: 0 0 auto;
  padding: 0 0 0 6px;
  line-height: 1;
  vertical-align: baseline;
  color: inherit;
  opacity: 0.7;
}
.ba-info:hover,
.ba-info:focus {
  opacity: 1;
  color: inherit;
}
.ba-view-toggle {
  white-space: nowrap;
}
.ba-card-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ba-badge {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 600;
}
.ba-card-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
}
.ba-subtitle {
  flex: 0 0 auto;
  margin: 0 0 8px;
  font-size: 13px;
  opacity: 0.8;
}
.ba-chart {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}
.ba-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.ba-state {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 0.8;
}
.ba-state-error {
  color: #c0392b;
  opacity: 1;
}
.ba-table th,
.ba-table td {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.ba-info-body {
  font-size: 13px;
  max-width: 280px;
}
.ba-info-body >>> p:last-child {
  margin-bottom: 0;
}
</style>

<!-- Unscoped: the theme class is on <body>. Shared with the Business Profiles page. -->
<style src="./accentHeader.css"></style>
